/**
 * One shape for a Philippine mobile number, everywhere.
 *
 * The field shows a fixed `+63` and the customer types the ten digits after it. That is
 * the whole reason this file exists: the old box accepted `09171234567`, `+639171234567`,
 * `639171234567` and `0917 123 4567`, and the customer had to decide which. Deciding is
 * work, and the customer this form is for is the one least equipped to do it.
 *
 * **Forgiving on the way in.** A number is pasted far more often than typed — from
 * Messenger, from Contacts, from a text — and it arrives in every one of those forms. So
 * anything recognisable is folded down to the ten national digits rather than refused,
 * and the customer never learns that the box had an opinion.
 */

/** Shown inside the input, not stored. The state holds the ten digits only. */
export const PH_DIALLING_CODE = '+63'

/** Mobile numbers here are ten digits and begin with 9. Landlines are not deliverable. */
const NATIONAL = /^9\d{9}$/

/**
 * Anything a person might paste, reduced to the ten digits after `+63`.
 *
 * The order of the strips matters. `+63` and `63` go before the leading zero, because
 * `639171234567` has no zero to strip and `09171234567` has no country code — testing for
 * the zero first would eat the `6` of a number that arrived complete.
 *
 * @param {string} raw
 * @returns {string} — up to ten digits, and never anything else
 */
export function toNational(raw) {
  let digits = String(raw ?? '').replace(/\D/g, '')

  if (digits.startsWith('63')) digits = digits.slice(2)
  else if (digits.startsWith('0')) digits = digits.slice(1)

  // Trailing digits past the tenth are a mistype or a doubled paste. Dropped silently:
  // there is nothing useful to say about them and the field is already the right length.
  return digits.slice(0, 10)
}

/** @returns {boolean} — whether the ten digits are a number the rider could dial */
export const isValidNational = (national) => NATIONAL.test(String(national ?? ''))

/**
 * The form's ten digits as the API wants them.
 *
 * `POST /api/orders` accepts `09…` and `+639…` alike and normalises to E.164 itself. This
 * sends E.164 anyway, because a number that leaves in the shape it will be stored in is
 * one fewer place for two representations to drift apart.
 *
 * @returns {string} — `+639171234567`, or `''` when there is nothing to send
 */
export const toE164 = (national) => {
  const digits = toNational(national)
  return digits ? `${PH_DIALLING_CODE}${digits}` : ''
}
